import { Button, CheckPicker, DateRangePicker, FormGroup, SelectPicker, Notification, Input, Modal, List } from "rsuite"
import styles from "./styles/rhChartPerEmployee.module.scss"
import { useEffect, useState } from "react";
import { ValueType } from "rsuite/lib/DateRangePicker";
import dayjs from "dayjs";
import axios from "../../../../utilities/axios";
import { ParametersGlobals } from "../../../../core/parametersGlobals/ParametersGlobals";
import ReactApexChart from "react-apexcharts";
import { CheckTreePicker } from "../../../../components/formfields/CheckTreePicker";

interface RHChartPerEmployeeProps {
    employeesData: { label: string; value: number }[]
    storesData: { label: string; value: number }[]
}

export default function RHChartPerEmployee({ employeesData, storesData }: RHChartPerEmployeeProps) {
    //Filtros
    const [selectedEmployee, setSelectedEmployee] = useState<number[]>([]);
    const [selectedErrors, setSelectedErrors] = useState<number[]>([]);
    const [selectedStore, setSelectedStore] = useState<number[]>([]);
    const [nomeLojaAplicada, setNomeLojaAplicada] = useState<string | null>(null);
    const [filteredEmployees, setFilteredEmployees] = useState(employeesData);
    const [selectedPeriod, setSelectedPeriod] = useState<ValueType | undefined>([
        dayjs().startOf('month').toDate(),
        dayjs().endOf('month').toDate(),
    ]);

    //Utilidades
    const [loading, setLoading] = useState<boolean>(false);
    const [formattedErrorsData, setFormattedErrorsData] = useState<{ value: number, label: string }[]>([]);
    const [chartState, setChartState] = useState<any>(null);
    const [detailsChartState, setDetailsChartState] = useState<any>(null);
    const [detailErrorSelected, setDetailErrorSelected] = useState<number | null>(null);
    const [modalOpen, setModalOpen] = useState<boolean>(false);
    const [modalData, setModalData] = useState<{ errorName: string; employees: { userName: string; count: number }[] } | null>(null);

    useEffect(() => {
        try {
            setLoading(true);

            fetchErrorsPerGroup();
        } catch (error) {
            console.log("Erro useEffect RHChartPerEmployee ::::>> ", error);
            Notification.error({ title: 'Erro', description: 'Erro ao buscar Lançamentos', placement: 'topEnd', close: true });
        } finally {
            setLoading(false);
        }
    }, [])

    useEffect(() => {
    setFilteredEmployees(employeesData);
}, [employeesData]);

    const fetchErrorsPerGroup = async () => {
        try {
            const response = await axios.get(`/api/v1/dashboard/chart/financial/custom/select-error-data`, {
                params: {
                    groupErrors: ParametersGlobals.defaultRHGroupErrorID
                }
            });

            const filteredErrors = response.data
                .filter((error: any) => error.error_active === true)
                .map((error: any) => ({
                    value: error.error_id,
                    label: error.error_name
                }));

            const defaultSelected = filteredErrors.map((error: any) => error.value);

            setFormattedErrorsData(filteredErrors);
            setSelectedErrors(defaultSelected);
        } catch (error) {
            console.log("Erro RHChartPerEmployee.fetchErrorsPerGroup ::::>> ", error);
            Notification.error({ title: 'Erro', description: 'Erro ao buscar Lançamentos', placement: 'topEnd', close: true });
        }
    };

    const clearFilters = () => {
        const allErrorIds = formattedErrorsData.map((err) => err.value);

        setSelectedErrors(allErrorIds);
        setSelectedEmployee([]);
        setSelectedPeriod([
            dayjs().startOf('month').toDate(),
            dayjs().endOf('month').toDate(),
        ]);
        setSelectedStore([]);
        setChartState(null);
        setDetailErrorSelected(null);
        setDetailsChartState(null);
        setNomeLojaAplicada(null);
    };

    const fetchChartData = async () => {
        if (!selectedPeriod || (!selectedEmployee && !selectedStore)) {
            Notification.warning({
                title: 'Aviso',
                description: 'Selecione um período e ao menos uma loja ou funcionário para filtrar',
                placement: 'topEnd',
                close: true
            });
            return;
        }

        try {
            setLoading(true);
            setDetailErrorSelected(null);
            setDetailsChartState(null);

            const { data } = await axios.get(`/api/v1/dashboard/rh/secondary/chart/employee`, {
                params: {
                    groupErrors: ParametersGlobals.defaultRHGroupErrorID,
                    employee: selectedEmployee,
                    errors: selectedErrors,
                    period: selectedPeriod,
                    store: selectedStore,
                }
            });

            // Atualizar os labels com contagem
            const formattedLabels = data.options.labels.map((label: string) => {
                const match = data.customData.find((item: any) => item.errorName === label);
                const count = match?.count ?? 0;
                return `${label}: ${count}`;
            });
            data.options.labels = formattedLabels;

            const updatedChartOptions = {
                ...data.options,
                chart: {
                    ...(data.options.chart || {}),
                    zoom: {
                        enabled: false,
                    },
                    events: {
                        dataPointSelection: function (event: any, chartContext: any, config: any) {
                            const index = config.dataPointIndex;
                            const clickedError = data.customData?.[index];

                            if (clickedError) {
                                 setModalData({
                                    errorName: clickedError.errorName,
                                    employees: clickedError.employees ?? []
                                });
                                setModalOpen(true);
                            }
                        }
                    }
                },
                tooltip: {
                    y: {
                        formatter: function () {
                            return ``;
                        },
                        title: {
                            formatter: (seriesName: any) => seriesName,
                        },
                    }
                }
            };

            setChartState({
                ...data,
                options: updatedChartOptions
            });
                let nomeLoja: string;
                if (selectedStore?.length) {
                    nomeLoja = selectedStore.map(value => storesData.find(item => item.value == value)?.label)?.join(' & ') ?? "Não identificada";
                } else {
                    nomeLoja = "Todas";
                }
                setNomeLojaAplicada(nomeLoja);

            }catch (error) {
            console.log("Erro RHChartPerEmployee.fetchChartData ::::>> ", error);
            Notification.error({
                title: 'Erro',
                description: 'Erro ao buscar dados',
                placement: 'topEnd',
                close: true
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <div className={styles.container}>
                <div className={styles.containerFilters}>
                    <div>
                        <FormGroup>
                            <label>Período</label>
                            <DateRangePicker
                                placeholder="Selecione uma data"
                                format="DD/MM/YYYY"
                                ranges={[]}
                                value={selectedPeriod}
                                onChange={(value) => setSelectedPeriod(value)}
                                className={styles.customDateRangePicker}
                            />
                        </FormGroup>

                        <FormGroup className={styles.customFilterFormGroup}>
                            <label>Funcionário</label>
                            <CheckTreePicker
                                api={{
                                    url: '/api/v1/users/select',
                                }}
                                placeholder="Selecione funcionários..."
                                value={selectedEmployee || undefined}
                                onChange={(value: any) => setSelectedEmployee(value)}
                                className={styles.customSelectPicker}
                                preventOverflow={false}
                                virtualized={false}
                            />
                        </FormGroup>

                        <FormGroup className={styles.customFilterFormGroup}>
                            <label>Loja</label>

                            <CheckTreePicker
                                api={{
                                    url: '/api/v1/stores/select',
                                }}
                                uses={{
                                    employees: selectedEmployee,
                                }}
                                displayLabel={"stores.store_name + ' (' + stores.store_acronym + ')'"}
                                placeholder="Selecione lojas..."
                                value={selectedStore || undefined}
                                onChange={(value: any) => setSelectedStore(value)}
                                className={styles.customSelectPicker}
                                preventOverflow={false}
                                virtualized={false}
                            />
                        </FormGroup>

                        <FormGroup>
                            <label>Grupo de Lançamentos</label>
                            <Input
                                value="RH"
                                style={{ width: 200 }}
                                disabled
                            />
                            <span className={styles.informationText}>
                                * Obrigatório
                            </span>
                        </FormGroup>

                        <FormGroup className={styles.customFilterFormGroup}>
                            <label>Lançamentos</label>
                            <CheckPicker
                                data={formattedErrorsData}
                                placeholder="Selecione os Lançamentos..."
                                cleanable
                                searchable
                                value={selectedErrors}
                                onChange={(value) => setSelectedErrors(value)}
                                className={styles.customSelectPicker}
                            />
                        </FormGroup>
                    </div>

                    <div>
                        <Button
                            color="cyan"
                            appearance="primary"
                            onClick={clearFilters}
                            loading={loading}
                            className={styles.customButton}
                        >
                            Limpar
                        </Button>

                        <Button
                            appearance="primary"
                            onClick={fetchChartData}
                            loading={loading}
                            className={styles.customButton}
                        >
                            Filtrar
                        </Button>
                    </div>
                </div>

                <div className={styles.containerCharts}>
                    {chartState ? (
                        <>
                            <div className={styles.secundaryChartCard}>
                                <h5 className={styles.chartTitle}>Por Funcionário</h5>

                                <ReactApexChart options={chartState?.options} series={chartState?.series} type="pie" height={500} />
                            {nomeLojaAplicada && (
                                    <div style={{ marginTop: '1rem', display: 'flex', justifyContent: 'center' }}>
                                        <span style={{ padding: '8px 16px', borderRadius: '8px', fontWeight: 600 }}>
                                            Loja: {nomeLojaAplicada}
                                        </span>
                                    </div>
                                )}
                            </div>
                        </>
                    ) : (
                        <div className={styles.noDataContainer}>
                            <p className={styles.noDataText}>Nenhum dado encontrado</p>
                        </div>
                    )}
                </div>
            </div>
            <Modal backdrop={true} show={modalOpen} onHide={() => setModalOpen(false)}>
                <Modal.Header>
                    <Modal.Title>Funcionários — {modalData?.errorName}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <List>
                        {modalData?.employees.map((emp, index) => (
                            <List.Item key={index} index={index}>
                                {emp.userName} — {emp.count} ocorrência{emp.count > 1 ? 's' : ''}
                            </List.Item>
                        ))}
                    </List>
                </Modal.Body>
                <Modal.Footer>
                    <Button onClick={() => setModalOpen(false)} appearance="subtle">Fechar</Button>
                </Modal.Footer>
            </Modal>
        </>
    )
}
